const SENSITIVE_PORTS = new Set(['21','22','23','25','53','80','139','143','3306','3389','8080']);

function toCamelCase(value = '') {
  return value
    .toString()
    .replace(/[^a-zA-Z0-9 ]+/g, ' ')
    .trim()
    .split(/\s+/)
    .map((word, index) => {
      if (!word) return '';
      const lower = word.toLowerCase();
      if (index === 0) {
        return lower;
      }
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join('');
}

function humanizeKey(key = '') {
  return key
    .toString()
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map(word => {
      if (!word) return word;
      if (word === word.toUpperCase()) {
        return word;
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}

function countArray(value) {
  return Array.isArray(value) ? value.length : 0;
}

function computeActiveDirectoryMetrics(adScan) {
  if (!adScan || typeof adScan !== 'object') return null;

  const result = {};

  if (Array.isArray(adScan['AD Users'])) {
    result['AD Users'] = adScan['AD Users'].length;
  }

  if (Array.isArray(adScan.inactive_accounts)) {
    result['orphaned_accounts_in_ad'] = adScan.inactive_accounts.length;
  }

  if (Array.isArray(adScan.domain_controllers)) {
    result['domain_controllers'] = adScan.domain_controllers.length;
  }

  if (Array.isArray(adScan.Computers)) {
    result['computers'] = adScan.Computers.length;
  }

  return Object.keys(result).length ? result : null;
}

function computeMicrosoft365Metrics(ms365) {
  if (!ms365 || typeof ms365 !== 'object') return null;

  const result = {};

  if (Array.isArray(ms365.users)) {
    result['users'] = ms365.users.length;
  }

  if (Array.isArray(ms365.inactive_accounts)) {
    result['orphaned_accounts_in_ms'] = ms365.inactive_accounts.length;
  }

  if (Array.isArray(ms365.MFA)) {
    const total = ms365.MFA.length;
    const disabled = ms365.MFA.filter(item => item && Number(item['MFA Enabled']) === 0).length;
    result['mfa_disabled'] = disabled;
    result['mfa_enabled'] = total - disabled;
  }

  return Object.keys(result).length ? result : null;
}

function computeNetworkMetrics(networkDevices) {
  if (!Array.isArray(networkDevices) || networkDevices.length === 0) return null;

  const deviceTypeCounts = {};
  let sensitivePorts = 0;
  let otherPorts = 0;

  networkDevices.forEach(device => {
    const type = device?.device_type || 'Unknown';
    deviceTypeCounts[type] = (deviceTypeCounts[type] || 0) + 1;

    if (device && device.open_ports && typeof device.open_ports === 'object') {
      Object.keys(device.open_ports).forEach(port => {
        if (SENSITIVE_PORTS.has(port)) {
          sensitivePorts += 1;
        } else {
          otherPorts += 1;
        }
      });
    }
  });

  const result = {};
  Object.entries(deviceTypeCounts).forEach(([type, count]) => {
    result[`devices_${type}`] = count;
  });

  if (sensitivePorts > 0) {
    result['sensitive_open_ports'] = sensitivePorts;
  }

  if (otherPorts > 0) {
    result['other_open_ports'] = otherPorts;
  }

  result['devices'] = networkDevices.length;

  return result;
}

function computeOnPremMetrics(onPrem) {
  if (!onPrem || typeof onPrem !== 'object') return null;

  const totalServers = countArray(onPrem.server_details);
  if (!totalServers) return null;

  const result = { servers: totalServers };

  if (Array.isArray(onPrem.outdated_servers)) {
    result['onprem_outdated_servers'] = onPrem.outdated_servers.length;
  }

  return result;
}

function computeLocalhostMetrics(localhost) {
  if (!localhost || typeof localhost !== 'object') return null;

  const result = {};

  const vuln = Array.isArray(localhost['External vulnerability']) ? localhost['External vulnerability'] : [];
  if (vuln.length) {
    let sensitivePorts = 0;
    let otherPorts = 0;

    vuln.forEach(entry => {
      if (entry && entry.open_ports && typeof entry.open_ports === 'object') {
        Object.keys(entry.open_ports).forEach(port => {
          if (SENSITIVE_PORTS.has(port)) {
            sensitivePorts += 1;
          } else {
            otherPorts += 1;
          }
        });
      }
    });

    if (sensitivePorts > 0) {
      result['sensitive_open_ports'] = sensitivePorts;
    }
    if (otherPorts > 0) {
      result['other_open_ports'] = otherPorts;
    }

    result['vulnerable_devices'] = vuln.length;
  }

  const hardware = localhost['Hardware Informations'];
  if (hardware && typeof hardware === 'object') {
    if (typeof hardware['CPU Available'] === 'number') {
      result['cpu_available'] = hardware['CPU Available'];
    }
    if (typeof hardware['RAM Available (GB)'] === 'number') {
      result['ram_available_gb'] = hardware['RAM Available (GB)'];
    }
  }

  return Object.keys(result).length ? result : null;
}

function computePiiMetrics(piiData) {
  if (!piiData || typeof piiData !== 'object') return null;

  const result = {};

  Object.entries(piiData).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      result[key] = value.length;
    } else if (typeof value === 'number') {
      result[key] = value;
    }
  });

  return Object.keys(result).length ? result : null;
}

function buildChartDataset(metricsObject) {
  if (!metricsObject || typeof metricsObject !== 'object') return null;

  const labels = [];
  const values = [];

  Object.entries(metricsObject).forEach(([key, value]) => {
    let numericValue;
    if (Array.isArray(value)) {
      numericValue = typeof value[0] === 'number' ? value[0] : undefined;
    } else if (typeof value === 'number') {
      numericValue = value;
    }

    if (typeof numericValue === 'number' && Number.isFinite(numericValue)) {
      labels.push(humanizeKey(key));
      values.push(numericValue);
    }
  });

  if (!labels.length) {
    return null;
  }

  return { labels, values };
}

export function computeDerivedMetrics(scanData = {}) {
  const coverData = {};
  const chartData = {};
  const chartMeta = {};

  const adMetrics = computeActiveDirectoryMetrics(scanData['Ad Scan']);
  if (adMetrics) {
    const section = 'Active Directory Scan';
    const chartKey = toCamelCase(section);
    coverData[section] = adMetrics;
    const dataset = buildChartDataset(adMetrics);
    if (dataset) {
      chartData[chartKey] = dataset;
      chartMeta[chartKey] = {
        label: `${section} Summary`,
        category: section,
        description: `Key counts extracted from ${section}`
      };
    }
  }

  const msMetrics = computeMicrosoft365Metrics(scanData['MS365']);
  if (msMetrics) {
    const section = 'Microsoft 365 Scan';
    const chartKey = toCamelCase(section);
    coverData[section] = msMetrics;
    const dataset = buildChartDataset(msMetrics);
    if (dataset) {
      chartData[chartKey] = dataset;
      chartMeta[chartKey] = {
        label: `${section} Summary`,
        category: section,
        description: `Key counts extracted from ${section}`
      };
    }
  }

  const networkMetrics = computeNetworkMetrics(scanData.network_devices);
  if (networkMetrics) {
    const section = 'Network Scan';
    const chartKey = toCamelCase(section);
    coverData[section] = networkMetrics;
    const dataset = buildChartDataset(networkMetrics);
    if (dataset) {
      chartData[chartKey] = dataset;
      chartMeta[chartKey] = {
        label: `${section} Summary`,
        category: section,
        description: `Device and port counts derived from ${section}`
      };
    }
  }

  const onPremMetrics = computeOnPremMetrics(scanData.Onprem);
  if (onPremMetrics) {
    const section = 'On-Prem Scan';
    const chartKey = toCamelCase(section);
    coverData[section] = onPremMetrics;
    const dataset = buildChartDataset(onPremMetrics);
    if (dataset) {
      chartData[chartKey] = dataset;
      chartMeta[chartKey] = {
        label: `${section} Summary`,
        category: section,
        description: `Server counts compiled from ${section}`
      };
    }
  }

  const localhostMetrics = computeLocalhostMetrics(scanData.localhost);
  if (localhostMetrics) {
    const section = 'Localhost Scan';
    const chartKey = toCamelCase(section);
    coverData[section] = localhostMetrics;
    const dataset = buildChartDataset(localhostMetrics);
    if (dataset) {
      chartData[chartKey] = dataset;
      chartMeta[chartKey] = {
        label: `${section} Summary`,
        category: section,
        description: `Localhost counts derived from scan data`
      };
    }
  }

  const piiMetrics = computePiiMetrics(scanData.PII);
  if (piiMetrics) {
    const section = 'Personally Identifiable Information';
    const chartKey = toCamelCase(section);
    coverData[section] = piiMetrics;
    const dataset = buildChartDataset(piiMetrics);
    if (dataset) {
      chartData[chartKey] = dataset;
      chartMeta[chartKey] = {
        label: `${section} Summary`,
        category: section,
        description: `PII counts collected from scan results`
      };
    }
  }

  return {
    coverData,
    chartData,
    chartMeta
  };
}
